import classNames from 'classnames';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { FormContext } from '../FormContext';
import { handleValidation, ValidatorRule } from '../validation';

type NAUInputProps = {
  name?: string;
  disabled?: boolean;
  label: string;
  description?: React.ReactNode;
  type: 'text' | 'email' | 'password';
  value?: string;
  defaultValue?: string;
  inputMode?:
    | 'text'
    | 'numeric'
    | 'decimal'
    | 'email'
    | 'search'
    | 'tel'
    | 'url';
  pattern?: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  validators?: ValidatorRule[];
};

const NAUInput: FC<NAUInputProps> = ({
  name,
  label,
  description,
  value = '',
  defaultValue,
  inputMode,
  pattern,
  placeholder,
  onChange,
  onKeyDown,
  onPaste,
  validators,
  type,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { registerInput, setInputError, registerValidation } =
    useContext(FormContext);

  // Register input and validation
  useEffect(() => {
    registerInput(label, inputRef);

    if (validators) {
      registerValidation(label, () => {
        if (disabled) return true;

        return handleValidation(
          inputRef.current?.value || '',
          validators,
          setErrorMessage,
          setInputError,
          label,
        );
      });
    }
  }, []);

  // Handle input change with validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e);

    if (validators) {
      handleValidation(
        e.target.value,
        validators,
        setErrorMessage,
        setInputError,
        label,
      );
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  // Common input props
  const commonInputProps = {
    disabled,
    required: validators?.includes('required'),
    value,
    defaultValue,
    inputMode,
    pattern,
    placeholder,
    ref: inputRef,
    onChange: handleInputChange,
    onKeyDown,
    onPaste,
    className: `form-control ${errorMessage ? 'is-invalid' : ''}`,
  };

  return (
    <div>
      <label
        className={classNames(
          'form-label',
          validators?.includes('required') && 'required',
        )}
      >
        {label}
        {description && (
          <span className="form-label-description">{description}</span>
        )}
      </label>

      {type === 'password' ? (
        <div className="input-group input-group-flat">
          <input
            {...commonInputProps}
            type={showPassword ? 'text' : 'password'}
            name={name}
          />
          <span className="input-group-text">
            {showPassword ? (
              <IconEyeOff
                role="button"
                stroke="currentColor"
                strokeWidth={2}
                fill="none"
                size={24}
                className="icon icon-1"
                onClick={togglePasswordVisibility}
              />
            ) : (
              <IconEye
                role="button"
                stroke="currentColor"
                strokeWidth={2}
                fill="none"
                size={24}
                className="icon icon-1"
                onClick={togglePasswordVisibility}
              />
            )}
          </span>
        </div>
      ) : (
        <input {...commonInputProps} type={type} name={name} />
      )}

      {errorMessage && <div className="invalid-feedback">{errorMessage}</div>}
    </div>
  );
};

export default NAUInput;
